import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useRealtimeEvent } from "../Realtime/RealtimeProvider";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

export type DeploymentLinks = {
	/** URL reellement servies en preview, resolues depuis le magasin deploye. */
	preview: string[];
	/** Idem pour la production. */
	production: string[];
	/** Chemin public que le contenu DEVRAIT avoir : repli honnete quand le magasin
	 * de lecture n'a pas encore de ligne pour lui. */
	expected: string[];
};

/**
 * URL de deploiement d'UN contenu, par son id stable, plus leur rafraichissement automatique.
 *
 * Pourquoi ce hook existe : le deploiement preview d'un contenu qu'on vient de creer est
 * ASYNCHRONE (le worker de preview draine la file quelques secondes plus tard). L'editeur
 * chargeait ces liens UNE SEULE FOIS au montage, avec des dependances qui ne bougeaient ni a
 * l'enregistrement ni a la fin du deploiement : le bouton « Dev » restait donc absent jusqu'a
 * ce qu'on recharge la page a la main. On ecoute desormais la fin de deploiement, exactement
 * comme la liste des contenus le fait deja pour ses pastilles.
 */
export const useDeploymentLinks = (contentId?: string | null) => {
	const [links, setLinks] = useState<DeploymentLinks | null>(null);
	const [version, setVersion] = useState(0);

	/** Force une relecture immediate (apres une publication, par exemple). */
	const refresh = useCallback(() => setVersion((current) => current + 1), []);

	useEffect(() => {
		if (!contentId) {
			setLinks(null);
			return;
		}
		let cancelled = false;
		axios
			.get<{ preview?: string[]; production?: string[]; expected?: string[] }>(`${apiBase}/api/content/deployment-links`, {
				params: { id: contentId },
				withCredentials: true
			})
			.then((response) => {
				if (cancelled) return;
				setLinks({
					preview: Array.isArray(response.data?.preview) ? response.data.preview : [],
					production: Array.isArray(response.data?.production) ? response.data.production : [],
					expected: Array.isArray(response.data?.expected) ? response.data.expected : []
				});
			})
			.catch(() => {
				if (!cancelled) setLinks(null);
			});
		return () => {
			cancelled = true;
		};
	}, [contentId, version]);

	// Une seule relecture par deploiement, a la fin : les ticks de progression ne changent rien
	// aux URL servies. Couvre le worker de preview (qui emet aussi « deploy »/finish) comme les
	// publications manuelles.
	useRealtimeEvent("deploy", (payload: any) => {
		if (payload?.phase === "finish") refresh();
	});

	return { links, refresh };
};

export default useDeploymentLinks;
